// Amortization Calculator Configuration
export const amortizationConfig = {
  title: "Amortization Calculator",
  inputs: [
    {
      id: "loanAmount",
      title: "Loan Amount",
      format: "currency",
    },
    {
      id: "loanTerm",
      title: "Loan Term",
      format: "number",
    },
    {
      id: "annualInterestRate",
      title: "Annual Interest Rate",
      format: "percentage",
    },
    {
      id: "dateOfFirstPayment",
      title: "Date of First Payment",
      format: "date",
    },
  ],
  outputs: [
    {
      id: "monthlyPayment",
      title: "Monthly Payment",
      format: "currency",
    },
    {
      id: "totalYearlyPayment",
      title: "Total Yearly Payment",
      format: "currency",
    },
    {
      id: "totalLifetimePayment",
      title: "Total Lifetime Payment",
      format: "currency",
    },
    {
      id: "totalLifetimeInterest",
      title: "Total Lifetime Interest",
      format: "currency",
    },
  ],
};

// Amortization Calculator Calculation Function
export const calculateAmortization = inputs => {
  const { loanAmount, annualInterestRate, loanTerm, dateOfFirstPayment } =
    inputs;

  const numberOfPayments = loanTerm * 12;
  const mir = annualInterestRate / 12 / 100;
  const monthlyPayment =
    loanTerm > 0 && annualInterestRate > 0
      ? Math.round(
          loanAmount * (mir / (1 - Math.pow(1 + mir, -numberOfPayments))) * 100,
        ) / 100
      : 0;
  const totalYearlyPayment = monthlyPayment * 12;
  const totalLifetimePayment = monthlyPayment * numberOfPayments;
  const totalLifetimeInterest = totalLifetimePayment - loanAmount;

  return {
    monthlyPayment,
    totalYearlyPayment,
    totalLifetimePayment,
    totalLifetimeInterest,
  };
};

/*
 * The following is an as-is copy of the current amortization calculator code and should not be
 * modified without discussion with Stewart.
 *
 * When it is modified, start and end the modification with a comment block indicating what was
 * changed and why.
 *
 * AUGUST 2025 / VELIR: This code was copied from the existing Stewart Title
 * codebase, almost entirely unchanged except for one class name. The new
 * React-powered calculator uses this code to generate the amortization schedule.
 * Any future changes should be tracked via comments as requested above.
 */

function formatAsMoney(amount, locale, currency) {
  return amount.toLocaleString(locale || "en-US", {
    style: "currency",
    currency: currency || "USD",
  });
}

function getNum(x) {
  if (typeof x === "undefined") {
    x = 0;
  }
  x = String(x);
  x = x.replace(/[^0-9.-]/g, "");
  x = parseFloat(x);
  if (Number.isNaN(x)) {
    x = 0;
  }
  return x;
}

function ammSchedule(principle, term, rate, start, isGroupByYear = false) {
  // standardize input
  principle = Math.round(getNum(principle) * 100);
  term = getNum(term);
  rate = Math.round(getNum(rate) * 1000000);
  if (rate >= 1000000) {
    rate /= 100; // change % to decimal
  }

  // static rate vars
  const monthlyRate = Math.round(rate / 12);
  const numPayments = term * 12;
  const pFactor = (1 + monthlyRate / 1000000) ** numPayments;
  const payment =
    pFactor === 1
      ? Math.ceil(principle / numPayments)
      : Math.ceil(
          (principle * ((monthlyRate * pFactor) / 1000000)) / (pFactor - 1),
        );
  start = start.split("-");
  const startDate = new Date(start[0], start[1], start[2]);
  const monthly = {
    principle,
    payment,
    month: startDate.getMonth(),
    year: startDate.getFullYear(),
  };
  const yearly = {
    real: 0,
    interest: 0,
  };

  // to check : need to localize
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const data = [];
  // Fill amortResultsTable with data
  for (let n = 1; n <= numPayments; n += 1) {
    // advance a year, if app.
    if (monthly.month === 12) {
      monthly.month = 0;
      monthly.year += 1;
      yearly.real = 0;
      yearly.interest = 0;
    }

    // set date output
    monthly.date = `${months[monthly.month]} ${monthly.year}`;
    yearly.date = `Year ${monthly.year}`;

    // calc interest
    monthly.interest = Math.round((monthly.principle * monthlyRate) / 1000000);
    yearly.interest += monthly.interest;
    // totalInterest += monthly.interest;

    // adjust last payment amount, if app.
    if (monthly.principle + monthly.interest < monthly.payment) {
      monthly.payment = monthly.principle + monthly.interest;
    }

    // calc principle paid & remaining balance
    monthly.real = monthly.payment - monthly.interest;
    monthly.principle -= monthly.real;
    yearly.real += monthly.real;
    yearly.principle = monthly.principle;
    if (isGroupByYear) {
      if (monthly.month === 11 || n === numPayments) {
        data.push([
          yearly.date,
          formatAsMoney(yearly.real / 100),
          formatAsMoney(yearly.interest / 100),
          formatAsMoney(yearly.principle / 100),
        ]);
      }
    } else {
      data.push([
        monthly.date,
        formatAsMoney(monthly.real / 100),
        formatAsMoney(monthly.interest / 100),
        formatAsMoney(monthly.principle / 100),
      ]);
    }
    monthly.month += 1;
  }
  return data;
}

function updateRow(row, data) {
  const tds = data.map(x => {
    // create a td
    const td = document.createElement("td");
    if (x != null) {
      td.innerText = x;
    }
    return td;
  });
  row.append(...tds);
}

function getValue(form, fieldname) {
  const el = form.querySelector(`[name="${fieldname}"]`);
  if (el) {
    return el.value;
  }
  return null;
}

export const printSchedule = (block, isGroupByYear = false) => {
  const fields = [
    "loanAmount",
    "loanTerm",
    "annualInterestRate",
    "dateOfFirstPayment",
  ];
  const form = block.querySelector("form");
  const table = block.querySelector("table.table-amortization");
  // august 2025
  // changed class name from .payments-div for clarity and cleaner CSS styling
  const div = block.querySelector(".js-payments-outer");
  // end change
  const [principle, term, rate, start] = [
    ...fields.map(x => getValue(form, x)),
  ];
  if (principle > 0 && term > 0 && rate > 0 && start) {
    div.classList.add("show");
    const tbody = table.querySelector("tbody");
    const rows = tbody.querySelectorAll("tr");
    const data = ammSchedule(principle, term, rate, start, isGroupByYear);
    const existing = Math.min(rows.length, data.length);
    let i = 0;
    for (i = 0; i < existing; i += 1) {
      rows[i].innerHTML = "";
      updateRow(rows[i], data[i]);
    }
    let lastElement = null;
    if (i > 0) {
      lastElement = rows[i - 1];
    }
    // remove extra rows
    for (; i < rows.length; i += 1) {
      rows[i].remove();
    }
    // create new rows
    for (; i < data.length; i += 1) {
      const newRow = document.createElement("tr");
      updateRow(newRow, data[i]);
      if (lastElement) {
        lastElement.insertAdjacentElement("afterend", newRow);
      } else {
        tbody.append(newRow);
      }
      lastElement = newRow;
    }
  } else {
    div.classList.remove("show");
  }
};
